import { createContext, useContext, useState, type ReactNode } from "react";
import { z } from "zod";

/* eslint-disable react-refresh/only-export-components -- the validated preference contract and hook share this provider's context */

export const ShellViewModeSchema = z.enum(["admin", "user"]);
export type ShellViewMode = z.infer<typeof ShellViewModeSchema>;

export const CONSULTANT_VIEW_MODE_STORAGE_KEY = "ccbox-consultant-view";

type ShellViewModeContextValue = {
  consultantViewMode: ShellViewMode;
  setConsultantViewMode: (viewMode: ShellViewMode) => void;
};

const ShellViewModeContext = createContext<ShellViewModeContextValue | null>(null);

function readStoredViewMode(): ShellViewMode {
  try {
    const parsed = ShellViewModeSchema.safeParse(
      window.localStorage.getItem(CONSULTANT_VIEW_MODE_STORAGE_KEY),
    );
    return parsed.success ? parsed.data : "admin";
  } catch {
    return "admin";
  }
}

export function ShellViewModeProvider({ children }: { children: ReactNode }) {
  const [consultantViewMode, setViewMode] = useState<ShellViewMode>(readStoredViewMode);

  function setConsultantViewMode(viewMode: ShellViewMode) {
    setViewMode(viewMode);
    try {
      window.localStorage.setItem(CONSULTANT_VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      // Storage is a convenience for this local preview preference. The
      // in-memory mode remains usable when the browser blocks persistence.
    }
  }

  return (
    <ShellViewModeContext.Provider value={{ consultantViewMode, setConsultantViewMode }}>
      {children}
    </ShellViewModeContext.Provider>
  );
}

export function useShellViewMode(): ShellViewModeContextValue {
  const context = useContext(ShellViewModeContext);
  if (!context) {
    throw new Error("useShellViewMode must be used inside ShellViewModeProvider");
  }
  return context;
}
